import { useState, useEffect } from 'react'
import { Box, Paper } from '@mui/material'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { ErrorBoundary } from 'Features/error/ErrorBoundary'
import { Header, Footer } from 'Common/Supplyvue-ui'
import { ShellProps } from './Layout.types'
import { brandLogoStyles } from './Layout.styles'
import { plannerTheme } from 'Common/Supplyvue-ui'
import brandLogo from 'Assets/images/planner-brand-logo.png'
import SwitchIcon from 'Assets/icons/switch.svg'
import { getUsername } from 'Features/auth/auth'
import { Breadcrumbs } from 'Common/Breadcrumbs/Breadcrumbs'

// Scheduler surfaces redesigned with their own teal brand bar (SchedulerBrandBar)
// render full-bleed — the shared Planner header/breadcrumb/footer would just
// duplicate that chrome underneath it. Keep this in sync with routes.tsx.
const isFullBleedRoute = (pathname: string) =>
  pathname === '/plan-scheduler' ||
  [
    '/plan-scheduler/published',
    '/plan-scheduler/manage-users',
    '/plan-scheduler/create',
    '/plan-scheduler/system-of-records',
  ].includes(pathname) ||
  /^\/plan-scheduler\/input\//.test(pathname)

export const Shell = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const tenantName = localStorage.getItem('tenantName') ?? ''
  const [loadingAuthData, setLoadingAuthData] = useState(true)
  const [userName, setUserName] = useState('')
  const fullBleed = isFullBleedRoute(location.pathname)

  useEffect(() => {
    const fetchUserName = async () => {
      const userName = await getUsername()
      setUserName(userName as string)
      setLoadingAuthData(false)
    }
    fetchUserName()
  }, [])

  useEffect(() => {
    // Check if we're in an auth redirect flow to prevent unnecessary redirects
    const isAuthRedirect =
      sessionStorage.getItem('authRedirectInProgress') === 'true'

    // Only redirect if not in authentication flow and not already on tenants page
    // ✅ USE NAVIGATE INSTEAD OF FULL PAGE RELOAD for faster navigation
    // Gate on `tenantId`, not `tenantName`: `tenantId` is what axios-client
    // sends as the `Tenant` header, so it's the value whose absence actually
    // breaks every API call. The two can diverge (partial clear, older build),
    // and gating on the display name let a tenant-less session through.
    const tenantId = localStorage.getItem('tenantId')
    if (!tenantId && !isAuthRedirect && location.pathname !== '/tenants') {
      navigate('/tenants', { replace: true })
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    // Don't clear localStorage if we're in an auth redirect flow
    const isAuthRedirect =
      sessionStorage.getItem('authRedirectInProgress') === 'true'

    if (window.location.pathname === '/tenants' && !isAuthRedirect) {
      localStorage.removeItem('tenantId')
      localStorage.removeItem('tenantsCount')
      localStorage.removeItem('tenantName')
    }
  }, [])

  const isTenantSwitchEnabled = (): boolean => {
    const count = localStorage.getItem('tenantsCount') as string
    const tenantsCount = parseInt(count)
    return tenantsCount > 1
  }

  if (loadingAuthData || loading) {
    return null
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      {!fullBleed && (
        <Header
          brandLogo={brandLogo}
          brandLogoStyles={brandLogoStyles}
          profileText={`Welcome ${userName}`}
          tenantName={tenantName}
          switchIcon={<SwitchIcon />}
          handleBrandLogoClick={() => tenantName && navigate('/')}
          handleTenantSwitchClick={() => navigate('/select-tenant')}
          isClickable={isTenantSwitchEnabled()}
        />
      )}
      <ErrorBoundary>
        <Box
          component="main"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 'auto',
            backgroundColor: fullBleed
              ? undefined
              : plannerTheme.palette.background.grey,
          }}
        >
          {!fullBleed && (
            <Paper
              sx={{
                padding: '16px 36px',
                boxShadow: '0 1px 6px 1px rgba(197,197,197,0.5)',
                borderRadius: 0,
              }}
            >
              <Box
                display="flex"
                width="100%"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Box width="50%">
                  <Breadcrumbs />
                </Box>
                <Box
                  width="50%"
                  display="flex"
                  flexDirection="row"
                  justifyContent="end"
                ></Box>
              </Box>
            </Paper>
          )}

          <Outlet />
        </Box>
      </ErrorBoundary>

      {!fullBleed && (
        <Footer
          copyrightText="All rights reserved"
          privacyText="Privacy Notice"
          cookiesText="Cookies"
          handlePrivacyClick={() =>
            window.open(
              'https://www.supplyvue.com/privacy-notice/',
              'SupplyVue',
              'noopener'
            )
          }
          handleCookiesClick={() =>
            window.open(
              'https://www.supplyvue.com/cookies/',
              'SupplyVue',
              'noopener'
            )
          }
        />
      )}
    </Box>
  )
}
